import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import type { AxiosError } from "axios";
import type { PaymentPlan } from "./usePaymentPlans";

export type CompanySubscription = {
    "@id": string;
    id: string;
    stripeSubscriptionId: string;
    status: "active" | "trialing" | "past_due" | "canceled" | "incomplete" | "incomplete_expired" | "unpaid";
    currentPeriodStart: string;
    currentPeriodEnd: string;
    trialStart?: string;
    trialEnd?: string;
    canceledAt?: string;
    cancelAtPeriodEnd?: string;
    paymentPlan: PaymentPlan | string;
};

type UseCurrentSubscriptionOptions = {
    isPaymentFeatureEnabled?: boolean;
};

export const useCurrentSubscription = (options?: UseCurrentSubscriptionOptions) => {
    const { selectedCompany } = useFoodpilotContext();
    const companyId = selectedCompany?.id;
    const isPaymentFeatureEnabled = options?.isPaymentFeatureEnabled ?? false;
    const canFetchCurrentSubscription = !!companyId && isPaymentFeatureEnabled;

    return useQuery<CompanySubscription | null>(
        ["current-subscription", companyId],
        async () => {
            if (!companyId) {
                return null;
            }

            try {
                const response = await loggedInApiCall.get(`/api/organizations/${companyId}/payment-plans/active`);
                return response.data;
            } catch (err) {
                const axiosError = err as AxiosError<any> | undefined;
                if (axiosError?.response?.status === 404) {
                    return null;
                }

                throw err instanceof Error ? err : new Error("Failed to fetch current subscription");
            }
        },
        {
            enabled: canFetchCurrentSubscription,
            staleTime: 60000,
            retry: (failureCount, error) => {
                const axiosError = error as AxiosError<any> | undefined;
                if (axiosError?.response?.status === 404) {
                    return false;
                }
                return failureCount < 1;
            },
        },
    );
};
